import { cache } from "react";
import api from "@/lib/api";
import { ApiRoutes } from "@/lib/routeResolver";
import {
  mapWebsitePackage,
  type HomePackage,
  type WebsitePackage,
} from "@/lib/navigation";

export interface TravelIdeasResult {
  packages: HomePackage[];
  totalPages: number;
  count: number;
}

const EMPTY: TravelIdeasResult = { packages: [], totalPages: 1, count: 0 };

const BRAND_ID = process.env.NEXT_PUBLIC_BRAND_ID_CENTRAL_HOLIDAYS!;
const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export const TRAVEL_IDEAS_PAGE_SIZE = 8;

/**
 * The backend does the filtering and paging, so `countryId` and `page` both go
 * upstream rather than narrowing an already-loaded list.
 */
function queryParams(page: number, pageSize: number, countryId?: string) {
  return {
    PageNumber: page,
    PageSize: pageSize,
    IsWebsite: true,
    IsTravelIdea: true,
    BrandId: BRAND_ID,
    ...(countryId ? { CountryId: countryId } : {}),
  };
}

function toResult(json: {
  data?: WebsitePackage[];
  totalPages?: number;
  count?: number;
}): TravelIdeasResult {
  return {
    packages: (json.data ?? []).filter((p) => p.pageURL).map(mapWebsitePackage),
    totalPages: json.totalPages ?? 1,
    count: json.count ?? 0,
  };
}

export const fetchTravelIdeas = cache(
  async (
    page = 1,
    pageSize = TRAVEL_IDEAS_PAGE_SIZE,
    countryId?: string,
  ): Promise<TravelIdeasResult> => {
    try {
      const res = await api.get(ApiRoutes.INVENTORY_PACKAGE_FOR_WEBSITE, {
        params: queryParams(page, pageSize, countryId),
      });
      return toResult(res?.data ?? {});
    } catch {
      return EMPTY;
    }
  },
);

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

export async function fetchTravelIdeasClient(
  page = 1,
  pageSize = TRAVEL_IDEAS_PAGE_SIZE,
  countryId?: string,
): Promise<TravelIdeasResult> {
  const params = new URLSearchParams({ CompanyId: COMPANY_ID });
  for (const [key, value] of Object.entries(
    queryParams(page, pageSize, countryId),
  )) {
    params.set(key, String(value));
  }

  const res = await fetch(
    `${BASE_URL}/${ApiRoutes.INVENTORY_PACKAGE_FOR_WEBSITE}?${params}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${TOKEN}`,
      },
    },
  );
  if (!res.ok) return EMPTY;
  return toResult(await res.json());
}
